import { FieldComponentType } from "@smart/bridge-intake-components-dom";
import { Avatar, ContextMenu } from "@smart/itops-sb-design-system-dom";

import {
  fieldComponentGroups,
  fieldComponentIcons,
  fieldComponentNames,
} from "../../constants";
import {
  FieldTemplate,
  GqlFieldValues,
  GqlGroupValues,
  GqlMatterLayout,
  GqlMatterType,
  LoadMatterFields,
} from "../../types";
import { MappedFieldPathDisplay } from "../mapped-field-path-display";

const FieldTypeIcon = ({
  fieldType,
  isMapped,
}: {
  fieldType: FieldComponentType;
  isMapped: boolean;
}) => (
  <Avatar
    size={24}
    icon={
      isMapped
        ? fieldComponentIcons.mapped.name
        : fieldComponentIcons[fieldType].name
    }
    bgColor={
      isMapped
        ? fieldComponentIcons.mapped.bgColor
        : fieldComponentIcons[fieldType].bgColor
    }
    tabIndex={-1}
  />
);

const FieldTypeDisplay = ({
  fieldType,
  isMapped,
}: {
  fieldType: FieldComponentType;
  isMapped: boolean;
}) => (
  <div className="flex items-center gap-small rounded">
    <FieldTypeIcon fieldType={fieldType} isMapped={isMapped} />
    <span className="text-ui font-semibold">
      {fieldComponentNames[fieldType]}
    </span>
  </div>
);

type FieldTypeIndicatorProps = {
  layout?: GqlFieldValues["layout"];
  matterField?: GqlFieldValues["field"];
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  groupLayout?: GqlGroupValues["layout"] | null;
  groupMatterField?: GqlGroupValues["field"] | null;
  onChangeFieldType: (newFieldType: FieldComponentType) => Promise<void>;
  excludingFieldTemplates: FieldTemplate[];
  fieldType: FieldComponentType;
  isDisabled?: boolean;
  allowChangeFieldType?: boolean;
};

const FieldTypeIndicator = ({
  matterField,
  layout,
  matterLayouts,
  matterTypes,
  loadMatterFields,
  groupMatterField,
  groupLayout,
  onChangeFieldType,
  excludingFieldTemplates,
  fieldType,
  isDisabled,
  allowChangeFieldType = true,
}: FieldTypeIndicatorProps) => {
  const isMapped = !!(matterField && layout);

  const menu = allowChangeFieldType ? (
    <ContextMenu
      id="change-field-type"
      placement="bottom-start"
      fallbackPlacements={["top-start"]}
      button={{
        variant: "secondarySubtle",
        size: "small",
        disabled: isDisabled,
        leftIcon: <FieldTypeIcon fieldType={fieldType} isMapped={isMapped} />,
        text: fieldComponentNames[fieldType],
        className: "p-[2px]",
      }}
      items={fieldComponentGroups
        .flatMap((group) => group.components)
        .filter(
          (c) =>
            c.type !== "group" &&
            !excludingFieldTemplates.find(({ type }) => type === c.type),
        )
        .map((c) => ({
          label: fieldComponentNames[c.type],
          labelOverride: (
            <FieldTypeDisplay fieldType={c.type} isMapped={false} />
          ),
          onClick: async () => onChangeFieldType(c.type),
        }))}
    />
  ) : (
    <div data-testid="field-type-readonly">
      <FieldTypeDisplay fieldType={fieldType} isMapped={isMapped} />
    </div>
  );

  return (
    <div className="flex items-center gap-4">
      {menu}
      {isMapped && (
        <MappedFieldPathDisplay
          field={matterField}
          layout={layout}
          matterLayouts={matterLayouts}
          matterTypes={matterTypes}
          loadMatterFields={loadMatterFields}
          groupMatterField={groupMatterField}
          groupLayout={groupLayout}
        />
      )}
    </div>
  );
};

export { FieldTypeIndicator, FieldTypeIndicatorProps };
